/**
 * /billing — Free vs Pro management.
 *
 * Free: comparison + "Upgrade to Pro" CTA that opens our own hosted checkout
 * in a new tab (Shieldz-on-Shieldz dogfood), then polls until the tier flips.
 * Pro: status + renewal (manual, crypto), custom domain with live Cloudflare
 * provisioning status. No tier ladder — just free and pro.
 */

import { useEffect, useRef, useState } from "react";
import {
  API_BASE,
  ApiError,
  getBilling,
  getCustomDomainStatus,
  removeCheckoutBg,
  setCheckoutBgScrim,
  setCustomDomain,
  uploadCheckoutBg,
  upgradeToBilling,
  type Billing as BillingT,
  type Scrim,
} from "../lib/api";
import { Banner, Btn } from "../components/ui";
import { Ic } from "../components/Icons";

type DomainData = { cname_target: string; status: string | null };

export function Billing() {
  const [billing, setBilling] = useState<BillingT | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradePending, setUpgradePending] = useState(false);
  const [justUpgraded, setJustUpgraded] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainData, setDomainData] = useState<DomainData | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [bgUrl, setBgUrl] = useState<string | null>(null);
  const [bgScrim, setBgScrim] = useState<Scrim>("dark");
  const [bgBusy, setBgBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const b = await getBilling();
        if (cancelled) return;
        setBilling(b);
        setDomainInput(b.custom_domain ?? "");
        setBgUrl(b.checkout_bg_url ?? null);
        setBgScrim(b.checkout_bg_scrim ?? "dark");
        if (b.tier === "pro" && b.custom_domain) void refreshDomainStatus();
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshDomainStatus() {
    try {
      const d = await getCustomDomainStatus();
      setDomainData({ cname_target: d.cname_target, status: d.status });
      // keep polling until the cert is issued
      if (d.status && d.status !== "active" && d.status !== "error" && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          try {
            const s = await getCustomDomainStatus();
            setDomainData({ cname_target: s.cname_target, status: s.status });
            if (!s.status || s.status === "active" || s.status === "error") {
              if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
              }
            }
          } catch {
            /* transient */
          }
        }, 8000);
      }
    } catch {
      /* leave as-is */
    }
  }

  // Poll billing after an upgrade until the watcher flips the tier to pro.
  const handleUpgrade = async () => {
    setUpgrading(true);
    setError(null);
    try {
      const { checkout_url } = await upgradeToBilling();
      window.open(checkout_url, "_blank", "noopener,noreferrer");
      setUpgradePending(true);
      const started = Date.now();
      const id = setInterval(async () => {
        if (Date.now() - started > 15 * 60 * 1000) {
          clearInterval(id);
          setUpgradePending(false);
          return;
        }
        try {
          const b = await getBilling();
          if (b.tier === "pro") {
            clearInterval(id);
            setBilling(b);
            setUpgradePending(false);
            setJustUpgraded(true);
          }
        } catch {
          /* keep polling */
        }
      }, 5000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setUpgrading(false);
    }
  };

  const handleBgFile = async (file: File | null | undefined) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setError("Image must be 4 MB or smaller.");
      return;
    }
    setBgBusy(true);
    setError(null);
    try {
      const res = await uploadCheckoutBg(file, bgScrim);
      setBgUrl(res.checkout_bg_url);
      setBgScrim(res.checkout_bg_scrim);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBgBusy(false);
    }
  };

  const handleScrim = async (s: Scrim) => {
    setBgScrim(s);
    if (!bgUrl) return; // nothing applied yet; will use it on next upload
    try {
      await setCheckoutBgScrim(s);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const handleRemoveBg = async () => {
    setBgBusy(true);
    setError(null);
    try {
      await removeCheckoutBg();
      setBgUrl(null);
      setBgScrim("dark");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBgBusy(false);
    }
  };

  const handleSaveDomain = async () => {
    setDomainSaving(true);
    setError(null);
    try {
      const domain = domainInput.trim() || null;
      const res = await setCustomDomain(domain);
      if (domain && res.cname_target) {
        setDomainData({ cname_target: res.cname_target, status: res.status ?? "pending_validation" });
        void refreshDomainStatus();
      } else {
        setDomainData(null);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setDomainSaving(false);
    }
  };

  if (error) {
    return (
      <div className="scroll">
        <div className="pad">
          <Banner tone="fail">{error}</Banner>
        </div>
      </div>
    );
  }
  if (!billing) {
    return (
      <div className="scroll">
        <div className="pad" style={{ color: "var(--ink-3)" }}>Loading…</div>
      </div>
    );
  }

  const tier = billing.tier;
  const pastDue = tier === "pro" && billing.status === "past_due";
  const renewsDate = billing.renews_at != null ? new Date(billing.renews_at) : null;

  return (
    <div className="scroll">
      <div className="pad fade-in" style={{ maxWidth: 720 }}>
        {justUpgraded && (
          <div style={{ marginBottom: 16 }}>
            <Banner tone="ok">Welcome to Pro 🎉 Your payment landed and your account is upgraded.</Banner>
          </div>
        )}
        {pastDue && (
          <div style={{ marginBottom: 16 }}>
            <Banner tone="warn">
              Your Pro period has lapsed{renewsDate ? ` (ended ${renewsDate.toLocaleDateString()})` : ""}. Renew to restore Pro features.
            </Banner>
          </div>
        )}

        {/* Current tier hero */}
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: tier === "pro" ? "var(--brand-700)" : "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                Current plan
              </div>
              <div style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.025em", lineHeight: 1 }}>
                {tier === "pro" ? "Pro" : "Free Forever"}
              </div>
              <div style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 8 }}>
                {tier === "pro"
                  ? `Status: ${billing.status}${renewsDate ? ` · ${pastDue ? "Ended" : "Renews"} ${renewsDate.toLocaleDateString()}` : ""}`
                  : "Unlimited usage. All features. 2 vertical ads on buyer checkout."}
              </div>
            </div>
            {(tier === "free" || pastDue) && (
              billing.pro_upgrades_enabled === false ? (
                <span style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 999, fontSize: 14, fontWeight: 600, color: "var(--ink-3)", background: "var(--surface-2)", border: "1px solid var(--line-2)" }}>
                  <Ic name="clock" size={15} /> Pro — coming soon
                </span>
              ) : (
                <Btn onClick={() => void handleUpgrade()} loading={upgrading} disabled={upgrading || upgradePending} style={{ flex: "none" }}>
                  <Ic name="shield" size={16} /> {pastDue ? "Renew Pro" : "Upgrade to Pro"} · ${billing.pro_price_usd}/mo
                </Btn>
              )
            )}
          </div>

          {upgradePending && (
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: "var(--ink-2)", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, padding: "12px 14px" }}>
              <span className="spinner" style={{ width: 14, height: 14, border: "2px solid var(--line-2)", borderTopColor: "var(--brand-600)", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite", flex: "none" }} />
              Finish paying in the checkout tab. This updates automatically the moment your payment lands.
            </div>
          )}
        </div>

        {/* Pro feature list */}
        <div className="s-label">What Pro unlocks</div>
        <div className="card card-pad">
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {billing.pro_features.map((feature, i) => (
              <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5 }}>
                <span style={{ width: 16, height: 16, borderRadius: 4, background: tier === "pro" && !pastDue ? "var(--brand-500)" : "var(--surface-2)", color: tier === "pro" && !pastDue ? "#fff" : "var(--ink-4)", display: "grid", placeItems: "center", flex: "none", marginTop: 2 }}>
                  <Ic name="check" size={11} />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Custom domain (Pro only) */}
        {tier === "pro" && (
          <>
            <div className="s-label">Custom domain</div>
            <div className="card card-pad">
              <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: "0 0 12px", lineHeight: 1.55 }}>
                Add a CNAME from your domain (e.g. <code className="mono">pay.brand.com</code>) to{" "}
                <code className="mono">{domainData?.cname_target ?? "cname.shieldz.cash"}</code> and your hosted
                checkout renders under your name with an auto-issued TLS certificate.
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input className="input mono" type="text" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder="pay.brand.com" style={{ flex: 1, minWidth: 240 }} autoComplete="off" spellCheck={false} />
                <Btn variant="secondary" onClick={() => void handleSaveDomain()} loading={domainSaving}>Save</Btn>
              </div>
              {billing.custom_domain && domainData && (
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <DomainStatusPill status={domainData.status} />
                  <span style={{ color: "var(--ink-3)" }}>
                    CNAME <code className="mono">{billing.custom_domain}</code> → <code className="mono">{domainData.cname_target}</code>
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Custom checkout background (Pro only) */}
        {tier === "pro" && (
          <>
            <div className="s-label">Custom checkout background</div>
            <div className="card card-pad">
              <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: "0 0 12px", lineHeight: 1.55 }}>
                Shows behind your hosted checkout. The payment card sits centered, so keep logos and focal
                points toward the edges. PNG, JPEG or WebP, up to 4 MB.
              </p>

              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={(e) => void handleBgFile(e.target.files?.[0])}
              />

              {bgUrl ? (
                <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                  <div
                    style={{
                      width: 160,
                      height: 90,
                      borderRadius: 10,
                      border: "1px solid var(--line)",
                      backgroundImage: `linear-gradient(${scrimCss(bgScrim)},${scrimCss(bgScrim)}), url(${API_BASE}${bgUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      flex: "none",
                    }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn variant="secondary" onClick={() => fileRef.current?.click()} loading={bgBusy}>Replace</Btn>
                    <Btn variant="ghost" onClick={() => void handleRemoveBg()} disabled={bgBusy} style={{ color: "var(--danger-ink)" }}>Remove</Btn>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={bgBusy}
                  style={{
                    width: "100%",
                    padding: "28px 16px",
                    borderRadius: 12,
                    border: "1.5px dashed var(--line-2)",
                    background: "var(--surface-2)",
                    color: "var(--ink-3)",
                    fontSize: 14,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Ic name="plus" size={20} />
                  {bgBusy ? "Uploading…" : "Click to upload an image"}
                  <span style={{ fontSize: 12, color: "var(--ink-4)" }}>Recommended 2560×1440 · max 4 MB</span>
                </button>
              )}

              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-3)", marginBottom: 8 }}>
                  Readability scrim
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {(["none", "dark", "light"] as Scrim[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void handleScrim(s)}
                      className={`set-seg-opt${bgScrim === s ? " on" : ""}`}
                      style={{ textTransform: "capitalize", flex: 1 }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12.5, color: "var(--ink-4)", margin: "8px 0 0", lineHeight: 1.5 }}>
                  A subtle dark or light layer keeps your header and footer readable on busy images.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Dogfood note — Free only */}
        {tier === "free" && (
          <div className="card" style={{ padding: 18, marginTop: 20, background: "var(--surface-2)", border: "1px solid var(--line)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>How upgrades work</div>
            <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: 0, lineHeight: 1.6 }}>
              We dogfood every flow we ship. Pro upgrades go through the same hosted checkout your customers use:
              you pay ${billing.pro_price_usd} in crypto (USDC, USDT, BTC, or shielded ZEC) and your tier flips for 30 days.
              It's a crypto subscription with no card on file, so you renew manually each month. No credit card, no Stripe.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function scrimCss(scrim: Scrim): string {
  return scrim === "light" ? "rgba(255,255,255,0.42)" : scrim === "none" ? "rgba(0,0,0,0)" : "rgba(5,19,13,0.5)";
}

function DomainStatusPill({ status }: { status: string | null }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: "Active", cls: "ok" },
    pending_validation: { label: "Issuing certificate…", cls: "warn" },
    pending: { label: "Pending DNS…", cls: "warn" },
    error: { label: "Error", cls: "fail" },
  };
  const m = (status && map[status]) || { label: status ?? "Not provisioned", cls: "warn" };
  return (
    <span className={`pill ${m.cls}`}>
      <span className="pd" />
      {m.label}
    </span>
  );
}
